import type { SummaryCreditRatingResponse, SummaryCreditRatingSlice } from '../api/chartDataTypes';
import { buildSummaryCreditRatingSummarySentence } from '../utils/summaryCreditRatingFormat';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { useSubmittedSelection } from '../utils/useSubmittedSelection';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { SUMMARY_CREDIT_RATING_SUBMIT_GUIDANCE, SummaryCreditRatingSelector } from './SummaryCreditRatingSelector';

type SummaryCreditRatingChartPanelProps = {
    response: SummaryCreditRatingResponse;
};

const PROMPT_SUMMARY = 'Select a credit rating and press Submit to display its percentage.';
const EMPTY_SUMMARY = 'No credit rating data available.';

/**
 * Summary Credit Rating orchestrator. Mirrors EconSectorChartPanel — a
 * point-in-time snapshot keyed by rating-bucket label rather than date. The
 * shell's banner opens with a prompt and the user tabs into the Data Series
 * dropdown, then presses Submit to display each bucket's weighting.
 */
export function SummaryCreditRatingChartPanel({ response }: SummaryCreditRatingChartPanelProps) {
    const { security, title, report_date, data } = response;
    const { slices } = data;

    const [selectedSlice, announcementKey, handleSelectedSliceChange] =
        useSubmittedSelection<SummaryCreditRatingSlice>();

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const hasSlices = slices.length > 0;

    let summary: string;
    let summaryHTML: string | undefined;
    if (!hasSlices) {
        summary = EMPTY_SUMMARY;
    } else if (selectedSlice === null) {
        summary = PROMPT_SUMMARY;
    } else {
        const parts = buildSummaryCreditRatingSummarySentence(selectedSlice);
        summary = parts.text;
        summaryHTML = parts.html;
    }

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}
            announcementKey={announcementKey}
            guidance={SUMMARY_CREDIT_RATING_SUBMIT_GUIDANCE}>
            {hasSlices ? (
                <SummaryCreditRatingSelector
                    slices={slices}
                    idPrefix='summary_credit_rating'
                    onSelectedSliceChange={handleSelectedSliceChange}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
